// Solution 1: Pre-rendered hidden forms - no AJAX needed, full TYPO3 context preserved

interface DownloadRequestBoxElement extends HTMLElement {
    dataset: {
        variant?: string;
        buttonIcon?: string;
        contentElementUid?: string;
        modalTitle?: string;
        modalDescription?: string;
        buttonCancel?: string;
        successTitle?: string;
        successMessage?: string;
        successCloseInfo?: string;
        buttonClose?: string;
        errorTitle?: string;
        errorConfigNotFound?: string;
        errorSubmissionFailed?: string;
        errorNoFormFound?: string;
        errorFormEmpty?: string;
        errorRecaptchaRequired?: string;
        finisherSuccess?: string;
        finisherError?: string;
    };
}

interface TriggerElement extends HTMLButtonElement {
    dataset: {
        modalTitle?: string;
        formPage?: string;
        successHeadline?: string;
        successMessage?: string;
        persistenceIdentifier?: string;
        downloadFile?: string;
        formUid?: string;
        enableExpiration?: string;
        lightboxHeadline?: string;
        lightboxDeactivateHeadline?: string;
        lightboxDownloadText?: string;
        lightboxDeactivateDownloadText?: string;
    };
}

class DownloadRequestBox {
    private element: DownloadRequestBoxElement;
    private triggerButton: TriggerElement | null;
    private modalContainer: HTMLElement | null;
    private isModalOpen = false;
    private showSuccess = false;
    // The live <form> node currently reparented into the modal, plus its original
    // container. We MOVE the real node (instead of copying its innerHTML) so the
    // reCAPTCHA widget and its evoweb JavaScript bindings keep working in the modal.
    private movedForm: HTMLFormElement | null = null;
    private formOrigin: HTMLElement | null = null;
    private escHandler: ((e: KeyboardEvent) => void) | null = null;

    constructor(element: DownloadRequestBoxElement) {
        this.element = element;
        this.triggerButton = element.querySelector('.download-request-box__trigger');

        // Create a unique modal container for this instance
        this.modalContainer = this.createModalContainer();

        // Check if page shows success state after form submission
        this.checkForSuccessState();

        if (this.triggerButton) {
            this.triggerButton.addEventListener('click', () => {
                this.openModal();
            });
        }

        this.attachDivClickHandler();
    }

    /**
     * click on .download-request-box__div triggert the Modal
     */
    private attachDivClickHandler(): void {
        const div = this.element.querySelector('.download-request-box__div');
        if (div && this.triggerButton) {
            div.addEventListener('click', () => {
                this.triggerButton?.click();
            });
        }
    }

    private createModalContainer(): HTMLElement {
        // Use content element UID as unique identifier for this modal
        const contentElementUid = this.element.dataset.contentElementUid || 'default';
        const containerId = `download-request-box__modal-container--${contentElementUid}`;

        // Check if modal container already exists in body
        let modalContainer = document.getElementById(containerId) as HTMLElement | null;

        if (!modalContainer) {
            // Create a new modal container and append to body (outside grid context)
            modalContainer = document.createElement('div');
            modalContainer.id = containerId;
            modalContainer.className = 'download-request-box__modal-container';
            modalContainer.style.display = 'none';
            // Append to body to avoid affecting grid layout
            document.body.appendChild(modalContainer);
        }

        return modalContainer;
    }

    private checkForSuccessState(): void {
        // Check if we're coming from a successful form submission
        const finisherSuccess = this.element.dataset.finisherSuccess;
        const finisherError = this.element.dataset.finisherError;

        if (finisherSuccess === '1') {
            // Show success modal automatically after successful form submission
            this.showSuccess = true;
            // Small delay to ensure DOM is ready
            setTimeout(() => {
                this.openModal();
                // Clean URL to prevent "resend form?" prompt on refresh
                this.cleanUrlAfterSubmission();
                // Reset hidden form so it's empty when opened again
                this.resetHiddenForm();
            }, 100);
        } else if (finisherError && finisherError.length > 0) {
            // Could implement error modal here if needed
        }
    }

    /**
     * Remove POST-related parameters from URL using history.replaceState
     * This prevents the browser's "resend form data?" prompt on refresh
     */
    private cleanUrlAfterSubmission(): void {
        const url = new URL(window.location.href);
        // Remove the action=perform parameter that triggers POST resubmission
        const keysToRemove: string[] = [];
        url.searchParams.forEach((_, key) => {
            if (key.includes('[action]') || key === 'cHash') {
                keysToRemove.push(key);
            }
        });
        keysToRemove.forEach((key) => url.searchParams.delete(key));
        // Also remove hash fragment that points to form
        url.hash = '';
        // Replace current history entry with clean URL
        window.history.replaceState({}, document.title, url.toString());
    }

    /**
     * Reset the hidden form to clear any previously entered values
     * This prevents the form from being prefilled when opened again
     */
    private resetHiddenForm(): void {
        const contentUid = this.element.dataset.contentElementUid;
        const hiddenFormContainer = (document.querySelector(`.download-request-box__hidden-form[data-content-element-uid="${contentUid}"]`) ||
            this.element.querySelector('.download-request-box__hidden-form')) as HTMLElement;

        if (!hiddenFormContainer) return;

        const form = hiddenFormContainer.querySelector('form') as HTMLFormElement;
        if (form) {
            form.reset();
        }
    }

    private openModal(): void {
        if (!this.modalContainer || !this.triggerButton) {
            console.error('❌ Missing modalContainer or triggerButton');
            return;
        }

        // Success view does not need the form at all — render success content and stop.
        if (this.showSuccess) {
            this.isModalOpen = true;
            this.modalContainer.style.display = 'block';
            this.modalContainer.innerHTML = this.getModalWithFormHTML();
            this.attachModalEventListeners();
            this.lockBodyScroll();
            return;
        }

        // Find the pre-rendered hidden form by UID (FluidComponents may move it to body)
        const contentUid = this.element.dataset.contentElementUid;
        const hiddenFormContainer = (document.querySelector(`.download-request-box__hidden-form[data-content-element-uid="${contentUid}"]`) ||
            this.element.querySelector('.download-request-box__hidden-form')) as HTMLElement;
        if (!hiddenFormContainer) {
            console.error('❌ No hidden form found! Make sure formvh:render is in template');
            const elementData = this.element?.dataset || {};
            const errorMessage = elementData.errorNoFormFound || 'No form found - check template configuration';
            this.modalContainer.innerHTML = this.getErrorHTML(errorMessage);
            this.modalContainer.style.display = 'block';
            return;
        }

        // Grab the LIVE <form> node. Moving (reparenting) it into the modal keeps the
        // reCAPTCHA widget and evoweb's form.js bindings alive — an innerHTML copy would
        // produce a dead reCAPTCHA that "loads forever" and can never be solved.
        const liveForm = hiddenFormContainer.querySelector('form') as HTMLFormElement | null;
        if (!liveForm) {
            console.error('❌ Hidden form is empty! Check formIdentifier and form configuration');
            const elementData = this.element?.dataset || {};
            const errorMessage = elementData.errorFormEmpty || 'Form is empty - check form configuration';
            this.modalContainer.innerHTML = this.getErrorHTML(errorMessage);
            this.modalContainer.style.display = 'block';
            return;
        }

        this.isModalOpen = true;
        this.modalContainer.style.display = 'block';

        // Build the modal shell with an empty form wrapper, then move the live form in.
        this.modalContainer.innerHTML = this.getModalWithFormHTML();
        const wrapper = this.modalContainer.querySelector('.download-request-box__form-wrapper') as HTMLElement | null;
        if (wrapper) {
            wrapper.appendChild(liveForm);
            this.movedForm = liveForm;
            this.formOrigin = hiddenFormContainer;
        }

        // Apply TÜV NORD design system styling to the form (idempotent — the form node
        // is persistent across opens, so per-open mutations guard against duplication).
        this.applyModalStylingToForm();

        // Add submit button if missing (TYPO3 form framework sometimes doesn't render it in our context)
        this.ensureSubmitButton();

        // Attach event listeners to the modal
        this.attachModalEventListeners();

        // Prevent body scroll while the modal is open
        this.lockBodyScroll();
    }

    private closeModal(): void {
        if (!this.modalContainer) return;

        this.isModalOpen = false;

        // Return the live form node to its hidden container BEFORE tearing down the modal,
        // otherwise clearing innerHTML would destroy it (and its reCAPTCHA state).
        if (this.movedForm && this.formOrigin) {
            this.formOrigin.appendChild(this.movedForm);
        }
        this.movedForm = null;
        this.formOrigin = null;

        if (this.escHandler) {
            document.removeEventListener('keydown', this.escHandler);
            this.escHandler = null;
        }

        this.modalContainer.style.display = 'none';
        this.modalContainer.innerHTML = '';

        // Re-enable body scroll and reset padding compensation
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';

        // Reset success state
        this.showSuccess = false;
    }

    private lockBodyScroll(): void {
        // Compensate for scrollbar width to prevent layout shift
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    private getModalWithFormHTML(): string {
        const elementData = this.element?.dataset || {};

        return `
            <div class="fixed inset-0 z-50 bg-darkblue/40 flex items-end md:items-center justify-center download-request-box__backdrop">
                <div class="bg-white rounded-none shadow-xl p-6 relative w-full md:max-w-[500px] lg:max-w-[600px] max-h-[90vh] overflow-y-auto" onclick="event.stopPropagation()">
                    ${
                        this.showSuccess
                            ? this.getSuccessContentHTML()
                            : `
                        <div class="download-request-box__form-wrapper"></div>
                        <!-- Hidden cancel button data for JS to use - ghost variant (text link style) -->
                        <template class="download-request-box__cancel-template">
                            <button
                                type="button"
                                class="download-request-box__close rounded max-w-full w-fit outline-none transition-all justify-center flex items-center overflow-hidden p-[14px] text-blue hover:text-blue-900 hover:underline"
                            >
                                <span class="font-medium text-sm lg:text-base">${elementData.buttonCancel || 'Cancel'}</span>
                            </button>
                        </template>
                    `
                    }
                </div>
            </div>
        `;
    }

    private getErrorHTML(message: string): string {
        const elementData = this.element?.dataset || {};
        const errorTitle = elementData.errorTitle || 'Fehler';
        const buttonClose = elementData.buttonClose || 'Schließen';

        return `
            <div class="fixed inset-0 z-50 bg-darkblue/40 flex items-end md:items-center justify-center download-request-box__backdrop">
                <div class="bg-white rounded-none shadow-xl p-6 relative w-full md:max-w-[500px] lg:max-w-[600px] max-h-[90vh] overflow-y-auto" onclick="event.stopPropagation()">
                    <div class="download-request-box__modal-content">
                        <h2 class="text-xl md:text-2xl lg:text-3xl font-bold text-darkblue mb-6">${errorTitle}</h2>
                        <p class="text-red-600 mb-6 text-base lg:text-lg leading-relaxed">${message}</p>
                        <div class="pt-4 border-t border-gray-100">
                            <button
                                type="button"
                                class="download-request-box__close rounded-lg max-w-full outline-none transition-all flex items-center overflow-hidden bg-blue border border-blue-700 text-white hover:bg-blue-800 px-6 py-3 justify-center"
                            >
                                <span class="font-medium text-sm lg:text-base">${buttonClose}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Returns the SUCCESS CONTENT only (not wrapped in modal).
     * Used inside getModalWithFormHTML() which provides the modal wrapper.
     */
    private getSuccessContentHTML(): string {
        // Get pre-rendered success content from the hidden container
        // Note: FluidComponents moves this element to body, so we find it by its UID attribute
        const contentUid = this.element.dataset.contentElementUid;
        let hiddenSuccessContainer =
            document.querySelector(`.download-request-box__hidden-success[data-content-element-uid="${contentUid}"]`) ||
            this.element.querySelector('.download-request-box__hidden-success');

        if (!hiddenSuccessContainer) {
            console.error('❌ Pre-rendered success container not found!');
            return `
                <p class="text-red-600">Fehler: Erfolgs-Template nicht gefunden</p>
                <button type="button" class="download-request-box__close mt-4 px-4 py-2 bg-blue text-white rounded">Schließen</button>
            `;
        }

        // Return just the content - modal wrapper is provided by caller
        return hiddenSuccessContainer.innerHTML;
    }

    /**
     * Returns FULL SUCCESS MODAL with backdrop wrapper.
     * Used when replacing the entire modal container content.
     */
    private getSuccessHTML(): string {
        return `
            <div class="fixed inset-0 z-50 bg-darkblue/40 flex items-end md:items-center justify-center download-request-box__backdrop">
                <div class="bg-white rounded-none shadow-xl p-6 relative w-full md:max-w-[500px] lg:max-w-[600px] max-h-[90vh] overflow-y-auto" onclick="event.stopPropagation()">
                    ${this.getSuccessContentHTML()}
                </div>
            </div>
        `;
    }

    /**
     * Apply minimal adjustments to the TYPO3 form in the modal
     * The form is already styled by TYPO3 Form Framework templates (Text.html, Field.html, etc.)
     * We only need to: 1) Ensure proper width, 2) Add Cancel button, 3) Swap submit icon to download
     */
    private applyModalStylingToForm(): void {
        if (!this.modalContainer) return;

        const form = this.modalContainer.querySelector('form') as HTMLFormElement;
        if (!form) return;

        // Prevent browser from prefilling form fields with previous submission data
        form.setAttribute('autocomplete', 'off');

        // The form HTML has nested containers from TYPO3 Form Framework:
        // wrapper -> section -> .section-container (has padding) -> div.grid -> form
        // Disable grids and remove extra padding
        const wrapper = this.modalContainer.querySelector('.download-request-box__form-wrapper') as HTMLElement;
        if (wrapper) {
            wrapper.style.cssText = 'width: 100% !important;';

            // Remove padding from section-container
            const sectionContainer = wrapper.querySelector('.section-container') as HTMLElement;
            if (sectionContainer) {
                sectionContainer.style.cssText = 'padding: 0 !important; margin: 0 !important;';
            }

            // Find and disable ALL inner grids
            const innerGrids = wrapper.querySelectorAll('.grid');
            innerGrids.forEach((grid) => {
                (grid as HTMLElement).style.cssText = 'display: block !important; width: 100% !important;';
            });
        }

        // Make form full width
        form.style.cssText = 'width: 100% !important; max-width: none !important;';

        // Fix fieldsets
        const fieldsets = form.querySelectorAll('fieldset');
        fieldsets.forEach((fieldset) => {
            (fieldset as HTMLElement).style.cssText = 'width: 100% !important; max-width: none !important;';
        });

        // Find and customize the navigation/button area
        const nav = form.querySelector('nav[role="toolbar"]');
        if (nav) {
            // Find the submit button and add Cancel button next to it
            const submitButton = nav.querySelector('button[type="submit"]') as HTMLElement;
            if (submitButton) {
                // Replace chevron icon with download icon
                this.replaceSubmitButtonIcon(submitButton);

                // Add Cancel button
                this.createButtonContainer(submitButton);
            }
        }
    }

    /**
     * Replace the chevron-right icon in submit button with download icon
     * Uses the existing SVG sprite system (lfc:icon component)
     */
    private replaceSubmitButtonIcon(button: HTMLElement): void {
        // Find the icon container and update aria-label
        const iconContainer = button.querySelector('i[role="img"][aria-label="chevron-right"]');
        if (iconContainer) {
            iconContainer.setAttribute('aria-label', 'download');

            // Find the SVG use element and change the sprite reference
            const useElement = iconContainer.querySelector('use');
            if (useElement) {
                const currentHref = useElement.getAttribute('xlink:href') || useElement.getAttribute('href') || '';
                // Replace #chevron-right with #download in the sprite URL
                const newHref = currentHref.replace('#chevron-right', '#download');
                useElement.setAttribute('xlink:href', newHref);
                useElement.setAttribute('href', newHref);
            }
        }
    }

    /**
     * Create a button container that holds Submit and Cancel buttons side by side
     */
    private createButtonContainer(submitButton: HTMLElement): void {
        if (!this.modalContainer) return;

        // Idempotency guard: the form node is reused across modal opens, so only wrap once.
        if (submitButton.closest('.download-request-box__button-wrapper')) {
            return;
        }

        // Find the cancel button template
        const cancelTemplate = this.modalContainer.querySelector('.download-request-box__cancel-template') as HTMLTemplateElement;
        if (!cancelTemplate) {
            console.warn('Cancel button template not found');
            return;
        }

        // Clone the cancel button from template
        const cancelButton = cancelTemplate.content.cloneNode(true) as DocumentFragment;
        const cancelButtonElement = cancelButton.querySelector('button');
        if (!cancelButtonElement) return;

        // Find the submit button's parent container (usually a toolbar or form-actions div)
        const submitParent = submitButton.parentElement;
        if (!submitParent) return;

        // Create a wrapper for both buttons
        const buttonWrapper = document.createElement('div');
        buttonWrapper.className = 'download-request-box__button-wrapper flex flex-col-reverse md:flex-row gap-4 pt-6 mt-6';

        // Move submit button into wrapper
        buttonWrapper.appendChild(submitButton);

        // Add cancel button to wrapper
        buttonWrapper.appendChild(cancelButtonElement);

        // Insert wrapper where submit button was
        submitParent.appendChild(buttonWrapper);

        // The cancel button carries the .download-request-box__close class and is wired up
        // centrally in attachModalEventListeners() (deduplicated per element).
    }

    private attachModalEventListeners(): void {
        if (!this.modalContainer) return;

        // Close buttons (cancel + success/error close). Bind once per element: some of these
        // live on the persistent form node and must not accumulate listeners across opens.
        this.modalContainer.querySelectorAll('.download-request-box__close').forEach((btn) => {
            const el = btn as HTMLElement;
            if (el.dataset.drbCloseBound === '1') return;
            el.dataset.drbCloseBound = '1';
            el.addEventListener('click', () => this.closeModal());
        });

        // Backdrop click (backdrop is part of the freshly-rendered shell each open)
        const backdrop = this.modalContainer.querySelector('.download-request-box__backdrop');
        if (backdrop) {
            backdrop.addEventListener('click', () => this.closeModal());
        }

        // TYPO3 form submission. The form node is persistent, so bind the submit handler once.
        const typo3Form = this.modalContainer.querySelector('form') as HTMLFormElement | null;
        if (typo3Form && typo3Form.dataset.drbSubmitBound !== '1') {
            typo3Form.dataset.drbSubmitBound = '1';
            typo3Form.addEventListener('submit', (e) => this.handleTYPO3FormSubmit(e));
        }

        // ESC key to close (tracked so it can be removed on any close, not just ESC)
        this.escHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this.isModalOpen) {
                this.closeModal();
            }
        };
        document.addEventListener('keydown', this.escHandler);
    }

    private handleTYPO3FormSubmit(e: Event): void {
        // Allow normal form submission - ForwardResponse in controller handles Form Framework processing

        const form = e.target as HTMLFormElement;
        const triggerData = this.triggerButton?.dataset || {};

        // Validate the form using TYPO3's built-in validation
        if (!form.reportValidity()) {
            e.preventDefault();
            return;
        }

        // reCAPTCHA guard: block submission until the challenge is solved, and show an
        // inline error. Server-side validation is the real gate, but this gives immediate
        // feedback and prevents a silent server rejection / page reload with no message.
        if (this.isRecaptchaSatisfied(form)) {
            this.clearRecaptchaError(form);
        } else {
            e.preventDefault();
            this.showRecaptchaError(form);
            return;
        }

        // Add additional data to the form before submission
        this.addHiddenFormFields(form, {
            contentElementUid: this.element.dataset.contentElementUid,
            downloadFile: triggerData.downloadFile,
            enableExpiration: triggerData.enableExpiration,
        });

        // Let the form submit normally - performAction() will forward to showAction()
        // This allows Form Framework to process the form and execute finishers
    }

    /**
     * Whether the reCAPTCHA challenge is satisfied for this form.
     * Returns true when no reCAPTCHA widget is present (e.g. development mode, captcha
     * disabled) so ordinary forms are never blocked. When a widget is present, the hidden
     * response field — filled by evoweb's onRecaptchaCallback once solved — must be non-empty.
     */
    private isRecaptchaSatisfied(form: HTMLFormElement): boolean {
        const widget = form.querySelector('.g-recaptcha');
        if (!widget) {
            return true;
        }
        const field = form.querySelector('[data-recaptcha-form-field]') as HTMLInputElement | null;
        return !!(field && field.value.trim() !== '');
    }

    private showRecaptchaError(form: HTMLFormElement): void {
        const message = this.element.dataset.errorRecaptchaRequired || 'Please confirm the reCAPTCHA before submitting.';

        let errorEl = form.querySelector('.download-request-box__recaptcha-error') as HTMLElement | null;
        if (!errorEl) {
            errorEl = document.createElement('p');
            errorEl.className = 'download-request-box__recaptcha-error text-red-600 text-sm lg:text-base mt-2';
            errorEl.setAttribute('role', 'alert');

            const widget = form.querySelector('.g-recaptcha');
            if (widget && widget.parentElement) {
                widget.parentElement.insertBefore(errorEl, widget.nextSibling);
            } else {
                form.appendChild(errorEl);
            }
        }
        errorEl.textContent = message;
    }

    private clearRecaptchaError(form: HTMLFormElement): void {
        const errorEl = form.querySelector('.download-request-box__recaptcha-error');
        if (errorEl) {
            errorEl.remove();
        }
    }

    private addHiddenFormFields(form: HTMLFormElement, data: Record<string, string | undefined>): void {
        Object.entries(data).forEach(([key, value]) => {
            if (value) {
                const existingField = form.querySelector(`input[name="${key}"]`);
                if (!existingField) {
                    const hiddenField = document.createElement('input');
                    hiddenField.type = 'hidden';
                    hiddenField.name = key;
                    hiddenField.value = value;
                    form.appendChild(hiddenField);
                }
            }
        });
    }

    private ensureSubmitButton(): void {
        if (!this.modalContainer) return;

        const form = this.modalContainer.querySelector('form');
        if (!form) return;

        // Check if submit button already exists
        const existingSubmitButton = form.querySelector('button[type="submit"], input[type="submit"]');
        if (existingSubmitButton) {
            return;
        }

        // Create submit button
        const submitButton = document.createElement('button');
        submitButton.type = 'submit';
        submitButton.className =
            'rounded-lg max-w-full outline-none transition-all flex items-center justify-center overflow-hidden bg-blue border border-blue-700 text-white hover:bg-blue-800 hover:border-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-blue-300 disabled:text-white disabled:border-blue-300 disabled:cursor-not-allowed px-6 py-3 lg:px-8 lg:py-4 w-full md:w-auto font-medium text-sm lg:text-base';
        submitButton.innerHTML = '<span class="font-medium text-sm lg:text-base">Absenden</span>';

        // Find the best place to insert the button (usually at the end of the form)
        const lastFieldset = form.querySelector('fieldset:last-of-type, .form-group:last-of-type');
        if (lastFieldset) {
            lastFieldset.appendChild(submitButton);
        } else {
            form.appendChild(submitButton);
        }
    }
}

// Initialize all Download Request Box instances on the page
export function initDownloadRequestBoxes(): void {
    // Check if already initialized by Storybook version
    if ((window as any).__downloadRequestBoxInitialized) {
        return;
    }

    const elements = document.querySelectorAll('[data-download-request-box]') as NodeListOf<DownloadRequestBoxElement>;

    elements.forEach((element, index) => {
        // Skip if element already has an instance
        if ((element as any).__downloadRequestBoxInstance) {
            return;
        }

        const instance = new DownloadRequestBox(element);
        (element as any).__downloadRequestBoxInstance = instance;
    });

    (window as any).__downloadRequestBoxInitialized = true;
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDownloadRequestBoxes);
} else {
    initDownloadRequestBoxes();
}
